import type { PageDesignModeElementInfo } from './pageDesignModeModel';

export const SOURCE_ATTRIBUTE = 'data-diagonal-source';

export type SourceMetadata = {
  filePath: string;
  lineNumber: number;
  columnNumber: number;
};

export function normalizeSourcePath(fileName: string): string {
  const normalized = String(fileName || '').replaceAll('\\', '/');
  const sourceIndex = normalized.lastIndexOf('/src/');

  return sourceIndex >= 0 ? normalized.slice(sourceIndex + 1) : normalized.replace(/^\/+/, '');
}

export function serializeSourceMetadata(
  fileName: string,
  lineNumber?: number,
  columnNumber?: number
): string {
  return JSON.stringify([normalizeSourcePath(fileName), lineNumber || 0, columnNumber || 0]);
}

export function parseSourceMetadata(value: string | null): SourceMetadata | null {
  if (value === null) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);

    if (
      !Array.isArray(parsed) ||
      typeof parsed[0] !== 'string' ||
      typeof parsed[1] !== 'number' ||
      typeof parsed[2] !== 'number'
    ) {
      return null;
    }

    return { filePath: parsed[0], lineNumber: parsed[1], columnNumber: parsed[2] };
  } catch {
    return null;
  }
}

export function pageSourcePath(pageId: string | number): string {
  return `src/pages/${pageId}.tsx`;
}

export function layoutSourcePath(layoutId: string | number): string {
  return `src/layouts/${layoutId}.tsx`;
}

export function componentSourcePath(name: string): string {
  return `src/components/${name}.tsx`;
}

export function readElementSourceMetadata(node: Element): SourceMetadata | null {
  const tagged = node.closest(`[${SOURCE_ATTRIBUTE}]`);

  return parseSourceMetadata(tagged?.getAttribute(SOURCE_ATTRIBUTE) ?? null);
}

export async function resolveTaggedElementInfo(
  node: HTMLElement
): Promise<PageDesignModeElementInfo> {
  return {
    tagName: node.tagName,
    componentName: null,
    source: readElementSourceMetadata(node)
  };
}
